import React, { useCallback, useEffect, useRef, useState } from 'react';
import data from '@emoji-mart/data';
import { init, SearchIndex } from 'emoji-mart';
import { twMerge } from 'tailwind-merge';
import ClickAwayListener from 'flow/components/ClickAwayListener';
import useEmojiLanguage from 'flow/hooks/useEmojiLanguage';
import { MessageContent } from 'flow/pages/messages/MessageInput';
import { isValidColonPosition } from 'flow/utils/chat-utils';

interface Props {
    messageContent: MessageContent;
    textAreaRef: React.RefObject<HTMLTextAreaElement>;
    updateMessage: (update: string, cursorPosition: number) => void;
}

const EmojiSearch = ({ textAreaRef, messageContent, updateMessage }: Props) => {
    const [emojiResults, setEmojiResults] = useState([]);
    const [showSearchResults, setShowSearchResults] = useState(false);
    const resultsRef = useRef<HTMLButtonElement>(null);
    const [focusedIndex, setFocusedIndex] = useState(0);
    const emojiContainerRef = useRef<HTMLDivElement | null>(null);
    const { emojiPickerI18n } = useEmojiLanguage();

    init({ data, emojiPickerI18n });

    // List of text sequences that should be treated as normal text, not triggering the emoji popup
    const exemptSequences = [
        ':-*',
        ":'",
        ';)',
        ':/',
        ':(',
        ':)',
        ':D',
        ':P',
        ':p',
        ':O',
        ':|',
        ':*',
    ];

    const handleEmojiSearch = useCallback(
        async (value: string) => {
            const cursorPosition = textAreaRef.current.selectionStart;

            // Check if the user has typed ":" and allow "+" after it for valid emoji sequences like ":+1"
            const lastColonIndex = value.lastIndexOf(':', cursorPosition - 1);
            if (lastColonIndex !== -1) {
                if (cursorPosition <= lastColonIndex) {
                    setShowSearchResults(false);
                    return;
                }

                if (!isValidColonPosition(lastColonIndex, value)) {
                    setShowSearchResults(false);
                    return;
                }

                const searchText = value.slice(
                    lastColonIndex + 1,
                    cursorPosition,
                );

                // Check if the typed sequence matches any exempt sequence
                const fullSequence = value.slice(
                    lastColonIndex,
                    cursorPosition,
                );
                if (exemptSequences.includes(fullSequence)) {
                    setShowSearchResults(false); // Don't trigger emoji popup
                    return;
                }

                // Allow letters, numbers, and "+" and "-1" for valid emoji search (e.g., ":+1")
                const isValidSearch = /^[\+\-a-zA-Z0-9]+$/.test(searchText);
                if (searchText.length > 0 && isValidSearch) {
                    const emojis = await SearchIndex.search(searchText);

                    setFocusedIndex(0);
                    setEmojiResults(emojis);
                    setShowSearchResults(emojis.length > 0);
                } else {
                    setShowSearchResults(false);
                    setFocusedIndex(-1);
                }
            } else {
                setShowSearchResults(false);
                setFocusedIndex(-1);
            }
        },
        [textAreaRef.current, messageContent],
    );

    useEffect(() => {
        handleEmojiSearch(messageContent.text);
    }, [messageContent.text]);

    const handleEmojiSelect = (emoji: string) => {
        const cursorPositionStart = textAreaRef.current.selectionStart;
        const cursorPositionEnd = textAreaRef.current.selectionEnd;
        const textBeforeCursor = messageContent.text.substring(
            0,
            cursorPositionStart,
        );
        const textAfterCursor =
            messageContent.text.substring(cursorPositionEnd);

        // Find the position of the last colon to replace the text with the emoji
        const lastColonIndex = textBeforeCursor.lastIndexOf(':');
        const newText =
            textBeforeCursor.slice(0, lastColonIndex) + emoji + textAfterCursor;

        const newCursorPosition = lastColonIndex + emoji.length;

        textAreaRef.current.focus();
        // Update the input text
        updateMessage(newText, newCursorPosition);

        // Hide search results
        setShowSearchResults(false);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
        if (!showSearchResults || emojiResults.length === 0) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setFocusedIndex((prevIndex) => {
                const nextIndex =
                    prevIndex < emojiResults.length - 1 ? prevIndex + 1 : 0;
                scrollToFocusedEmoji(nextIndex);
                return nextIndex;
            });
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setFocusedIndex((prevIndex) => {
                const nextIndex =
                    prevIndex > 0 ? prevIndex - 1 : emojiResults.length - 1;
                scrollToFocusedEmoji(nextIndex);
                return nextIndex;
            });
        } else if (e.key === 'Enter' && focusedIndex !== -1) {
            e.preventDefault();
            e.stopPropagation();
            handleEmojiSelect(emojiResults[focusedIndex].skins[0].native);
        } else if (
            e.key === 'Escape' ||
            e.key === 'ArrowLeft' ||
            e.key === 'ArrowRight'
        ) {
            e.preventDefault();
            e.stopPropagation();
            setShowSearchResults(false);
        }
    };

    const scrollToFocusedEmoji = (index: number) => {
        if (emojiContainerRef.current) {
            const emojiItem = emojiContainerRef.current.querySelector(
                `[data-index="${index}"]`,
            );
            if (emojiItem) {
                emojiItem.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                });
            }
        }
    };

    useEffect(() => {
        textAreaRef.current?.addEventListener('keydown', handleKeyDown);

        return () => {
            textAreaRef.current?.removeEventListener('keydown', handleKeyDown);
        };
    }, [emojiResults, focusedIndex, showSearchResults]);

    return (
        <>
            {showSearchResults && (
                <ClickAwayListener
                    onClickAway={() => setShowSearchResults(false)}
                >
                    <div
                        ref={emojiContainerRef}
                        className="scrollbar shadow-base absolute inset-x-0 bottom-14 z-30 max-h-40 w-full overflow-hidden rounded-t-sm bg-gray-100 p-2"
                    >
                        {emojiResults?.map((emoji, index) => (
                            <button
                                type="button"
                                key={emoji.id}
                                data-index={index} // Used to scroll to the focused emoji
                                ref={resultsRef}
                                onClick={() =>
                                    handleEmojiSelect(emoji.skins[0].native)
                                }
                                className={twMerge(
                                    'flex w-full items-center gap-3 p-2 hover:bg-gray-200',
                                    index === focusedIndex ? 'bg-gray-300' : '',
                                )}
                            >
                                <span>{emoji.skins[0].native}</span>
                                <span>:{emoji.id}:</span>
                            </button>
                        ))}
                    </div>
                </ClickAwayListener>
            )}
        </>
    );
};

export default React.memo(EmojiSearch);
